/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Games Hub - Practice games landing page
 *
 * Provides access to all interactive learning games:
 * - Word of the Day (inline)
 * - Math Term Matching Game (links to /games/math-matching)
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Gamepad2, BookOpen, Timer, Puzzle, Lock, ChevronRight } from 'lucide-react'
import WordOfTheDayWidget from '../components/dashboard/WordOfTheDayWidget'
import vocabService from '../services/vocabService'

const GameCard = ({ title, description, icon: Icon, iconColor, stats, cta, onClick, disabled }) => (
  <div
    className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center gap-4 transition-shadow ${
      disabled ? 'opacity-60' : 'hover:shadow-md'
    }`}
  >
    <div
      className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl"
      style={{ backgroundColor: `${iconColor}20` }}
    >
      <Icon className="w-6 h-6" style={{ color: iconColor }} />
    </div>

    <div className="flex-1 min-w-0">
      <div className="font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      {description && (
        <div className="hidden md:block text-sm text-gray-500 dark:text-gray-400">
          {description}
        </div>
      )}
    </div>

    {stats && (
      <div className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5 max-w-[200px] shrink-0">
        {stats}
      </div>
    )}

    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors min-w-[110px] justify-center ${
        disabled
          ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
          : 'text-white bg-primary-600 hover:bg-primary-700'
      }`}
    >
      {disabled ? (
        <>
          <Lock className="w-3.5 h-3.5" />
          Coming Soon
        </>
      ) : (
        <>
          {cta}
          <ChevronRight className="w-4 h-4" />
        </>
      )}
    </button>
  </div>
)

const Games = () => {
  const navigate = useNavigate()
  const [mathTermCount, setMathTermCount] = useState(null)

  useEffect(() => {
    vocabService
      .getMathTerms(1)
      .then((terms) => {
        if (terms?.length > 0) setMathTermCount(true)
      })
      .catch(() => {})
  }, [])

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Gamepad2 className="w-7 h-7 text-primary-600 dark:text-primary-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Practice Games</h1>
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          Sharpen your SAT skills while having fun. Play daily to build vocabulary and reinforce key
          concepts.
        </p>
      </div>

      {/* Word of the Day -- full inline widget */}
      <div className="mb-8">
        <WordOfTheDayWidget />
      </div>

      {/* Game rows */}
      <div className="space-y-3">
        <GameCard
          title="Math Term Match"
          description="Match math terms with their definitions. Test your speed and accuracy across SAT math vocabulary."
          icon={Puzzle}
          iconColor="#8b5cf6"
          stats={mathTermCount ? 'Normal mode (6 pairs) or Hard mode (12 pairs)' : null}
          cta="Play Now"
          onClick={() => navigate('/games/math-matching')}
        />

        <GameCard
          title="Speed Vocab"
          description="Race against the clock to answer SAT vocabulary-in-context questions. 15 seconds per question, 10 rounds."
          icon={Timer}
          iconColor="#f59e0b"
          stats="Craft and Structure domain — Words in Context skill"
          cta="Play Now"
          onClick={() => navigate('/games/speed-vocab')}
        />

        <GameCard
          title="Grammar Fix"
          description="Practice Standard English Conventions. Answer grammar and punctuation questions with detailed explanations."
          icon={BookOpen}
          iconColor="#10b981"
          stats="Boundaries and Form, Structure, and Sense skills"
          cta="Play Now"
          onClick={() => navigate('/games/grammar-fix')}
        />

        <GameCard
          title="Context Clues"
          description="Read passage excerpts and answer comprehension questions. Build your streak of correct answers."
          icon={BookOpen}
          iconColor="#3b82f6"
          stats="Information and Ideas domain — Central Ideas, Details, Inferences"
          cta="Play Now"
          onClick={() => navigate('/games/context-clues')}
        />
      </div>
    </div>
  )
}

export default Games
